/**
 * Parse Local Date Utility
 * 
 * Safely parses DATE-only strings (YYYY-MM-DD) as local calendar dates
 * without timezone shifting.
 * 
 * Problem: new Date("YYYY-MM-DD") parses as UTC midnight, which shifts
 * to previous day in negative timezone offsets (e.g., PST -8 hours).
 * 
 * Solution: Parse manually to create local Date object with no time component.
 * 
 * @param {string|null|undefined} dateString - Date string in YYYY-MM-DD format
 * @returns {Date|null} - Local Date object or null if invalid
 */
export function parseLocalDate(dateString) {
  if (!dateString || typeof dateString !== 'string') {
    return null;
  }
  
  // Match YYYY-MM-DD format
  const dateMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!dateMatch) {
    return null;
  }
  
  const [, yearStr, monthStr, dayStr] = dateMatch;
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10) - 1; // JavaScript months are 0-indexed
  const day = parseInt(dayStr, 10);
  
  // Validate parsed values
  if (isNaN(year) || isNaN(month) || isNaN(day)) {
    return null;
  }
  
  // Create local Date object (no timezone conversion)
  // This ensures the date stays on the correct calendar day regardless of timezone
  const date = new Date(year, month, day);
  
  // Validate the created date (handles invalid dates like Feb 30)
  if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) {
    return null;
  }
  
  return date;
}

/**
 * Check if a date string is DATE-only (no time component)
 * @param {string} dateString - Date string to check
 * @returns {boolean} - True if date-only format (YYYY-MM-DD)
 */
export function isDateOnly(dateString) {
  if (!dateString || typeof dateString !== 'string') {
    return false;
  }
  // Match YYYY-MM-DD format (exactly, no time component)
  return /^\d{4}-\d{2}-\d{2}$/.test(dateString);
}
